import { chakra } from '@chakra-ui/react';
import { Portal } from '@chakra-ui/react';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Heading } from '../primitives/Heading';
import { focusRing } from '../primitives/_shared';

const Backdrop = chakra('div', {
  base: {
    position: 'fixed',
    inset: 0,
    bg: 'overlay.backdrop',
    zIndex: 'modal',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: '4',
    animation: 'ehe-fade-in 120ms ease-out',
  },
});

const Surface = chakra('div', {
  base: {
    position: 'relative',
    width: 'full',
    maxHeight: 'calc(100dvh - 2rem)',
    display: 'flex',
    flexDirection: 'column',
    bg: 'surface.raised',
    color: 'text.primary',
    borderRadius: 'xl',
    borderWidth: '1px',
    borderColor: 'border.default',
    boxShadow: 'lg',
    animation: 'ehe-dialog-in 160ms cubic-bezier(0.2,0,0,1)',
  },
});

const Head = chakra('div', {
  base: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '4',
    px: '6',
    pt: '5',
    pb: '3',
  },
});

const Body = chakra('div', { base: { px: '6', py: '2', overflowY: 'auto', minHeight: 0 } });

const Footer = chakra('div', {
  base: { display: 'flex', justifyContent: 'flex-end', gap: '2', px: '6', pt: '4', pb: '5', flexWrap: 'wrap' },
});

const CloseButton = chakra('button', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSize: '8',
    mt: '-1',
    mr: '-2',
    flexShrink: 0,
    borderRadius: 'md',
    color: 'text.muted',
    bg: 'transparent',
    border: 'none',
    cursor: 'pointer',
    _hover: { bg: 'action.secondary', color: 'text.primary' },
    ...focusRing,
  },
});

type DialogSize = 'xs' | 'sm' | 'md' | 'lg';
const maxWidth: Record<DialogSize, string> = {
  xs: '22rem',
  sm: '28rem',
  md: '34rem',
  lg: '44rem',
};

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  /** Footer actions (buttons). Rendered right-aligned. */
  footer?: ReactNode;
  size?: DialogSize;
  /** Hide the header close (X) button (e.g. a forced-decision confirm). */
  hideClose?: boolean;
  'data-testid'?: string;
  /** Element focused on open; defaults to the dialog surface. */
  initialFocusRef?: React.RefObject<HTMLElement | null>;
}

/**
 * A modal dialog: portalled, backdrop + Escape + close-button dismiss, focus moved in on
 * open and restored to the trigger on close, `role="dialog"` + `aria-modal` with the
 * title wired as the accessible name. Body scrolls within a capped height so long
 * content never pushes the page.
 */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'sm',
  hideClose,
  initialFocusRef,
  ...rest
}: DialogProps) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Hold `onClose` in a ref so the open-effect can call the latest handler without
  // depending on it. Callers pass a fresh inline `onClose` every render (#293); if the
  // main effect depended on it, it would re-run each render — re-focusing the surface and
  // stealing focus from an input on every keystroke. The ref is updated in its own effect.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  const close = () => onCloseRef.current();

  // Runs only on the open/close transition (deps: [open]) — never on incidental
  // re-renders. Auto-focus, scroll-lock, the Escape listener, and return-focus-on-close
  // therefore each fire exactly once per open. `initialFocusRef` is a stable ref object
  // in practice, so capturing it at open time is correct.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    const id = requestAnimationFrame(() => {
      (initialFocusRef?.current ?? surfaceRef.current)?.focus();
    });
    // Lock body scroll while the modal is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      cancelAnimationFrame(id);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus?.();
    };
    // Deps intentionally [open] only: `onClose` is read through `onCloseRef` and
    // `initialFocusRef` is a stable ref object, so this runs once per open/close (#293).
  }, [open]);

  if (!open) return null;

  return (
    <Portal>
      <Backdrop
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) close();
        }}
      >
        <Surface
          ref={surfaceRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          maxW={maxWidth[size]}
          {...rest}
        >
          <Head>
            <Heading level={2} size="md" id={titleId}>
              {title}
            </Heading>
            {!hideClose && (
              <CloseButton type="button" aria-label="Close" onClick={close}>
                <X size={18} strokeWidth={2} aria-hidden />
              </CloseButton>
            )}
          </Head>
          <Body>{children}</Body>
          {footer && <Footer>{footer}</Footer>}
        </Surface>
      </Backdrop>
    </Portal>
  );
}
